import React, { useEffect, useMemo, useState } from 'react';
import { admin } from '../services/api';
import { toast } from '../hooks/use-toast';
import ClampWithMore from './ClampWithMore';
import {
  Gavel, Search, Loader2, Edit3, Trash2, Timer, PackageOpen, XCircle, ChevronDown,
  ChevronRight, Users, DollarSign, Clock, Save, Info, AlertTriangle,
} from 'lucide-react';

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'pending_review', label: 'Pending review' },
  { key: 'sold', label: 'Sold' },
  { key: 'dealbin', label: 'Deal Bin' },
  { key: 'expired', label: 'Expired' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'cancelled', label: 'Cancelled' },
];

const STATUS_STYLE = {
  active: 'bg-green-500/10 text-green-300 border-green-500/30',
  pending_review: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30',
  sold: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  dealbin: 'bg-orange-500/10 text-orange-300 border-orange-500/30',
  expired: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
  rejected: 'bg-red-500/10 text-red-300 border-red-500/30',
  cancelled: 'bg-red-500/10 text-red-300 border-red-500/30',
};

export default function AdminAuctionsPanel() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [editModal, setEditModal] = useState(null); // full form state
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (q.trim()) params.q = q.trim();
      const data = await admin.auctions(params);
      setRows(data);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const toggleExpand = async (aid) => {
    if (expandedId === aid) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(aid); setDetail(null); setDetailLoading(true);
    try {
      const d = await admin.auctionDetail(aid);
      setDetail(d);
    } catch (err) {
      toast({ title: 'Detail load failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setDetailLoading(false); }
  };

  const doClose = async (aid) => {
    setBusy(`${aid}:close`);
    try {
      const r = await admin.closeAuction(aid);
      toast({ title: `Closed — ${r.outcome}` });
      await load();
    } catch (err) { toast({ title: 'Close failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(null); }
  };

  const doExtend = async (aid) => {
    const raw = window.prompt('Extend this auction by how many hours?', '24');
    if (!raw) return;
    const h = parseInt(raw, 10);
    if (Number.isNaN(h) || h <= 0) return;
    setBusy(`${aid}:extend`);
    try {
      const r = await admin.extendAuction(aid, h);
      toast({ title: `Extended by ${h}h`, description: `Now ends ${new Date(r.expiresAt).toLocaleString()}` });
      await load();
    } catch (err) { toast({ title: 'Extend failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(null); }
  };

  const doMoveDealBin = async (aid) => {
    if (!window.confirm('Move this item to the Deal Bin? Any current bidders will be refunded their bid points.')) return;
    setBusy(`${aid}:dealbin`);
    try {
      const r = await admin.moveToDealBin(aid);
      toast({ title: 'Moved to Deal Bin', description: `${r.biddersNotified} bidder(s) refunded ${r.refundedPts} pts total.` });
      await load();
    } catch (err) { toast({ title: 'Move failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(null); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    const reason = confirmDel.reason?.trim() || undefined;
    setBusy(`${confirmDel.aid}:delete`);
    try {
      const r = await admin.deleteAuction(confirmDel.aid, reason);
      toast({ title: 'Auction deleted', description: `${r.biddersNotified} bidder(s) refunded ${r.refundedPts} pts total.` });
      setConfirmDel(null);
      await load();
    } catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(null); }
  };

  const submitEdit = async () => {
    if (!editModal?.aid) return;
    setBusy(`${editModal.aid}:edit`);
    const patch = {};
    ['title','description','status','deliveryMethod','deliveryDescription','pickupInstructions','productSize','productColor','productCondition','productBrand','productSpecs']
      .forEach(k => { if (editModal[k] !== undefined && editModal[k] !== null) patch[k] = editModal[k]; });
    ['estimatedValue','setPrice','startingPrice','dealBinPrice','shippingCost','taxes','handling','deliveryFee']
      .forEach(k => { if (editModal[k] !== '' && editModal[k] !== undefined && editModal[k] !== null) patch[k] = Number(editModal[k]); });
    if (editModal.expiresAt) patch.expiresAt = editModal.expiresAt;
    try {
      await admin.editAuction(editModal.aid, patch);
      toast({ title: 'Auction updated' });
      setEditModal(null);
      await load();
    } catch (err) { toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(null); }
  };

  const openEdit = (r) => setEditModal({
    aid: r.id, title: r.title || '', description: r.description || '',
    estimatedValue: r.estimatedValue ?? '', setPrice: r.setPrice ?? '',
    startingPrice: r.startingPrice ?? '', dealBinPrice: r.dealBinPrice ?? '',
    shippingCost: r.shippingCost ?? 0, taxes: r.taxes ?? 0, handling: r.handling ?? 0,
    deliveryMethod: r.deliveryMethod || 'pickup', deliveryFee: r.deliveryFee ?? 0,
    deliveryDescription: r.deliveryDescription || '',
    pickupInstructions: r.pickupInstructions || '',
    productSize: r.productSize || '', productColor: r.productColor || '',
    productCondition: r.productCondition || '', productBrand: r.productBrand || '',
    productSpecs: r.productSpecs || '',
    status: r.status || 'active',
    expiresAt: r.expiresAt ? r.expiresAt.slice(0,16) : '',
  });

  const totalPts = useMemo(() => rows.reduce((s, r) => s + (r.totalBidPts || 0), 0), [rows]);

  return (
    <div className="space-y-4" data-testid="admin-auctions-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            placeholder="Search by title, description, donor…"
            data-testid="admin-auction-search"
            className="w-full bg-black border border-[#1f1f1f] rounded-lg pl-9 pr-3 py-2 text-sm" />
        </div>
        <button onClick={load} disabled={loading}
          className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-black text-xs font-semibold disabled:opacity-60 inline-flex items-center gap-1">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Search
        </button>
        <div className="text-xs text-gray-500 ml-auto">{rows.length} listings · {totalPts.toLocaleString()} pts wagered</div>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((s) => (
          <button key={s.key} onClick={() => setStatus(s.key)}
            data-testid={`admin-auction-status-${s.key}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${status === s.key ? 'bg-green-500 text-black border-green-500' : 'border-[#2a2a2a] text-gray-300 hover:border-green-500/50'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading auctions…</div>
      ) : rows.length === 0 ? (
        <div className="pbb-card p-10 text-center">
          <Gavel className="mx-auto text-green-400" size={26}/>
          <div className="mt-3 font-semibold">No auctions match this filter</div>
        </div>
      ) : (
        <div className="pbb-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0d1f14] text-left text-xs text-gray-400 uppercase">
              <tr>
                <th className="px-3 py-3 w-6"></th>
                <th className="px-3 py-3">Item</th>
                <th className="px-3 py-3">Donor</th>
                <th className="px-3 py-3">Retail / Winner pays</th>
                <th className="px-3 py-3">Bids · Pts</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Ends</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <React.Fragment key={r.id}>
                  <tr data-testid={`admin-auction-row-${r.id}`} className="border-t border-[#1f1f1f] hover:bg-[#0d1a12]/60">
                    <td className="px-3 py-3 align-top">
                      <button onClick={() => toggleExpand(r.id)} data-testid={`admin-auction-expand-${r.id}`} className="text-gray-500 hover:text-white">
                        {expandedId === r.id ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                      </button>
                    </td>
                    <td className="px-3 py-3 max-w-[260px] align-top">
                      <div className="font-semibold text-white pbb-card-title" title={r.title}>{r.title}</div>
                      <ClampWithMore text={r.description} lines={2} className="text-[11px] text-gray-500 mt-1" testId={`admin-auction-desc-${r.id}`} />
                    </td>
                    <td className="px-3 py-3 text-gray-300 text-xs align-top">
                      <div>{r.donorName || '—'}</div>
                      <div className="text-gray-500">{r.donorId?.slice(0, 8) || ''}</div>
                    </td>
                    <td className="px-3 py-3 text-xs align-top">
                      <div className="text-gray-400">Retail ${Number(r.estimatedValue || 0).toFixed(2)}</div>
                      <div className="text-emerald-300 font-semibold">Winner ${Number(r.setPrice || 0).toFixed(2)}</div>
                    </td>
                    <td className="px-3 py-3 text-xs align-top">
                      <div className="inline-flex items-center gap-1 text-white"><Users size={12}/> {r.bidCount || 0}</div>
                      <div className="text-gray-500">{(r.totalBidPts || 0).toLocaleString()} pts</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_STYLE[r.status] || 'bg-gray-500/10 text-gray-300 border-gray-500/30'}`}>
                        {r.status || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-[11px] text-gray-400 align-top">
                      {r.expiresAt ? new Date(r.expiresAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-3 text-right align-top">
                      <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                        <button onClick={() => openEdit(r)} data-testid={`admin-auction-edit-${r.id}`}
                          className="px-2 py-1 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-[11px] inline-flex items-center gap-1">
                          <Edit3 size={11}/> Edit
                        </button>
                        {r.status === 'active' && (
                          <>
                            <button onClick={() => doExtend(r.id)} disabled={busy === `${r.id}:extend`} data-testid={`admin-auction-extend-${r.id}`}
                              className="px-2 py-1 rounded-lg border border-[#2a2a2a] hover:border-blue-500 text-blue-300 text-[11px] inline-flex items-center gap-1 disabled:opacity-50">
                              <Timer size={11}/> Extend
                            </button>
                            <button onClick={() => doClose(r.id)} disabled={busy === `${r.id}:close`} data-testid={`admin-auction-close-${r.id}`}
                              className="px-2 py-1 rounded-lg border border-[#2a2a2a] hover:border-yellow-500 text-yellow-300 text-[11px] inline-flex items-center gap-1 disabled:opacity-50">
                              <XCircle size={11}/> Close
                            </button>
                            <button onClick={() => doMoveDealBin(r.id)} disabled={busy === `${r.id}:dealbin`} data-testid={`admin-auction-dealbin-${r.id}`}
                              className="px-2 py-1 rounded-lg border border-[#2a2a2a] hover:border-orange-500 text-orange-300 text-[11px] inline-flex items-center gap-1 disabled:opacity-50">
                              <PackageOpen size={11}/> Deal Bin
                            </button>
                          </>
                        )}
                        <button onClick={() => setConfirmDel({ aid: r.id, title: r.title, bidCount: r.bidCount, reason: '' })}
                          data-testid={`admin-auction-delete-${r.id}`}
                          className="px-2 py-1 rounded-lg border border-[#2a2a2a] hover:border-red-500 text-red-300 text-[11px] inline-flex items-center gap-1">
                          <Trash2 size={11}/> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === r.id && (
                    <tr className="border-t border-[#1f1f1f] bg-black/40">
                      <td colSpan={8} className="px-6 py-4">
                        {detailLoading ? (
                          <div className="text-xs text-gray-400 flex items-center gap-2"><Loader2 className="animate-spin" size={12}/> Loading bids…</div>
                        ) : detail && detail.auction?.id === r.id ? (
                          <div>
                            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Bids on this item ({detail.bids.length})</div>
                            {detail.bids.length === 0 ? (
                              <div className="text-xs text-gray-500">No bids yet.</div>
                            ) : (
                              <table className="w-full text-xs" data-testid={`admin-auction-bids-${r.id}`}>
                                <thead className="text-gray-500">
                                  <tr>
                                    <th className="text-left py-1">Bidder</th>
                                    <th className="text-left py-1">Amount</th>
                                    <th className="text-left py-1">Cumulative</th>
                                    <th className="text-left py-1">Status</th>
                                    <th className="text-left py-1">When</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detail.bids.map(b => (
                                    <tr key={b.id} className="border-t border-[#151515]">
                                      <td className="py-1.5 text-white">{b.bidderName || b.bidderId?.slice(0,8)}</td>
                                      <td className="py-1.5"><span className="text-yellow-300 font-semibold">{b.amount} pts</span></td>
                                      <td className="py-1.5 text-gray-400">{b.cumulativeAt || '—'}</td>
                                      <td className="py-1.5">
                                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${b.status === 'winning' ? 'bg-green-500/10 text-green-300' : b.status === 'refunded' ? 'bg-blue-500/10 text-blue-300' : 'bg-gray-500/10 text-gray-400'}`}>{b.status}</span>
                                      </td>
                                      <td className="py-1.5 text-gray-500">{b.createdAt ? new Date(b.createdAt).toLocaleString() : ''}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* EDIT MODAL */}
      {editModal && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-[#0f0f0f] border border-[#1f1f1f] rounded-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="font-semibold flex items-center gap-2"><Edit3 size={16}/> Edit auction</div>
              <button onClick={() => setEditModal(null)} className="text-gray-400 hover:text-white"><XCircle size={18}/></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <F label="Title"><input className="pbb-input" value={editModal.title} onChange={e => setEditModal({ ...editModal, title: e.target.value })}/></F>
              <F label="Description"><textarea rows={3} className="pbb-input" value={editModal.description} onChange={e => setEditModal({ ...editModal, description: e.target.value })}/></F>
              <F label="Delivery or pickup instructions (sent to winner after payment)"><textarea rows={3} className="pbb-input" data-testid="admin-auction-pickup-instructions" value={editModal.pickupInstructions} onChange={e => setEditModal({ ...editModal, pickupInstructions: e.target.value })}/></F>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <F label="Retail (USD)"><input type="number" className="pbb-input" value={editModal.estimatedValue} onChange={e => setEditModal({ ...editModal, estimatedValue: e.target.value })}/></F>
                <F label="Winner pays (USD)"><input type="number" className="pbb-input" value={editModal.setPrice} onChange={e => setEditModal({ ...editModal, setPrice: e.target.value })}/></F>
                <F label="Start pts"><input type="number" className="pbb-input" value={editModal.startingPrice} onChange={e => setEditModal({ ...editModal, startingPrice: e.target.value })}/></F>
                <F label="Deal-bin price"><input type="number" className="pbb-input" value={editModal.dealBinPrice} onChange={e => setEditModal({ ...editModal, dealBinPrice: e.target.value })}/></F>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <F label="Shipping"><input type="number" className="pbb-input" value={editModal.shippingCost} onChange={e => setEditModal({ ...editModal, shippingCost: e.target.value })}/></F>
                <F label="Taxes"><input type="number" className="pbb-input" value={editModal.taxes} onChange={e => setEditModal({ ...editModal, taxes: e.target.value })}/></F>
                <F label="Handling"><input type="number" className="pbb-input" value={editModal.handling} onChange={e => setEditModal({ ...editModal, handling: e.target.value })}/></F>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Status">
                  <select className="pbb-input" value={editModal.status} onChange={e => setEditModal({ ...editModal, status: e.target.value })}>
                    {['active','pending_review','sold','dealbin','expired','rejected','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </F>
                <F label="Ends at (UTC)">
                  <input type="datetime-local" className="pbb-input" value={editModal.expiresAt} onChange={e => setEditModal({ ...editModal, expiresAt: e.target.value })}/>
                </F>
              </div>
              <div className="text-[11px] text-gray-500 flex items-start gap-1"><Clock size={11} className="mt-0.5"/> "Ends at" is interpreted as UTC. Current UTC: {new Date().toISOString().slice(0,16).replace('T',' ')}.</div>
              <div className="text-[11px] text-gray-500 flex items-start gap-1"><Info size={11} className="mt-0.5"/> If you change Retail without setting Winner pays, the storefront tier formula recomputes Winner pays automatically.</div>
            </div>
            <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end gap-2">
              <button onClick={() => setEditModal(null)} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 text-sm">Cancel</button>
              <button onClick={submitEdit} disabled={busy === `${editModal.aid}:edit`} data-testid="admin-auction-edit-submit"
                className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-black text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1">
                {busy === `${editModal.aid}:edit` ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {confirmDel && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-red-500/30 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="font-semibold flex items-center gap-2 text-red-300"><AlertTriangle size={16}/> Delete auction</div>
              <button onClick={() => setConfirmDel(null)} className="text-gray-400 hover:text-white"><XCircle size={18}/></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p><b className="text-white">{confirmDel.title}</b> will be permanently deleted.</p>
              {confirmDel.bidCount > 0 ? (
                <p className="text-yellow-300"><Users className="inline" size={12}/> {confirmDel.bidCount} bidder(s) will be refunded all their bid points and notified.</p>
              ) : (
                <p className="text-gray-500">No bidders to refund.</p>
              )}
              <F label="Reason (shown in the refund notification)">
                <input className="pbb-input" value={confirmDel.reason} onChange={e => setConfirmDel({ ...confirmDel, reason: e.target.value })}
                  placeholder="e.g. Duplicate listing / Policy violation"/>
              </F>
            </div>
            <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 text-sm">Cancel</button>
              <button onClick={doDelete} disabled={busy === `${confirmDel.aid}:delete`} data-testid="admin-auction-delete-confirm"
                className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1">
                {busy === `${confirmDel.aid}:delete` ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>} Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
